import { decodeLegacyEmotionGraph, LEGACY_EMOTION_CELLS } from "./emotion-map-topology";
import type { EmotionMapIndividualState, EmotionMapRecord } from "./emotion-map";

export interface ConversationEmotionNode {
  cell_id: number;
  label: string;
  layer: number;
  activation: number;
  delta: number | null;
  trend: "rising" | "steady" | "falling" | "unknown";
}

export interface ConversationEmotionIndividual {
  active_nodes: ConversationEmotionNode[];
  source_balance: {
    situation: number;
    reaction: number;
    dominant: "situation" | "reaction" | "balanced";
  };
}

export interface ConversationEmotionState {
  causal_id: string;
  tick_index: number;
  race_time_ms: number;
  delta_interval_ms: number | null;
  driver: ConversationEmotionIndividual;
  vehicle: ConversationEmotionIndividual;
}

const MAX_ACTIVE_NODES = 12;
const TREND_EPSILON = 2 / 255;

const maximumOriginStrength = (individual: EmotionMapIndividualState, origin: "situation" | "reaction"): number =>
  Math.max(...Object.values(individual[origin].cells));

function sourceBalance(individual: EmotionMapIndividualState): ConversationEmotionIndividual["source_balance"] {
  const situation = maximumOriginStrength(individual, "situation");
  const reaction = maximumOriginStrength(individual, "reaction");
  return {
    situation,
    reaction,
    dominant: Math.abs(situation - reaction) <= TREND_EPSILON
      ? "balanced"
      : situation > reaction ? "situation" : "reaction",
  };
}

function activeNodes(
  current: EmotionMapIndividualState,
  previous: EmotionMapIndividualState | null,
): ConversationEmotionNode[] {
  const currentGraph = decodeLegacyEmotionGraph(current.legacy_graph);
  const previousGraph = previous ? decodeLegacyEmotionGraph(previous.legacy_graph) : null;
  if (!currentGraph) return [];
  const candidates = LEGACY_EMOTION_CELLS.map(cell => {
    const activation = currentGraph.membranePotentials[cell.id] ?? 0;
    const previousActivation = previousGraph?.membranePotentials[cell.id];
    const delta = previousActivation === undefined ? null : activation - previousActivation;
    return {
      cell_id: cell.id,
      label: cell.label,
      layer: cell.layer,
      activation,
      delta,
      trend: delta === null ? "unknown" as const
        : delta > TREND_EPSILON ? "rising" as const
          : delta < -TREND_EPSILON ? "falling" as const : "steady" as const,
    };
  }).filter(node => node.activation > 0);
  const ranked = [...candidates].sort((left, right) =>
    right.activation - left.activation
    || Math.abs(right.delta ?? 0) - Math.abs(left.delta ?? 0)
    || left.cell_id - right.cell_id);
  const selected: ConversationEmotionNode[] = [];
  for (const layer of [...new Set(LEGACY_EMOTION_CELLS.map(cell => cell.layer))].sort((a, b) => a - b)) {
    const strongest = ranked.find(node => node.layer === layer);
    if (strongest) selected.push(strongest);
  }
  for (const node of ranked) {
    if (selected.length >= MAX_ACTIVE_NODES) break;
    if (!selected.some(selectedNode => selectedNode.cell_id === node.cell_id)) selected.push(node);
  }
  return selected.sort((left, right) =>
    right.activation - left.activation
    || Math.abs(right.delta ?? 0) - Math.abs(left.delta ?? 0)
    || left.cell_id - right.cell_id);
}

export function buildConversationEmotionState(
  current: EmotionMapRecord,
  previous: EmotionMapRecord | null,
): ConversationEmotionState {
  const compatiblePrevious = previous?.car_id === current.car_id
    && previous.race_time_ms < current.race_time_ms ? previous : null;
  return {
    causal_id: current.causal_id,
    tick_index: current.tick_index,
    race_time_ms: current.race_time_ms,
    delta_interval_ms: compatiblePrevious ? current.race_time_ms - compatiblePrevious.race_time_ms : null,
    driver: {
      active_nodes: activeNodes(current.driver, compatiblePrevious?.driver ?? null),
      source_balance: sourceBalance(current.driver),
    },
    vehicle: {
      active_nodes: activeNodes(current.vehicle, compatiblePrevious?.vehicle ?? null),
      source_balance: sourceBalance(current.vehicle),
    },
  };
}
